import * as React from "react";
import { format, parse } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface DateTimePickerProps {
  value?: string; // YYYY-MM-DDTHH:mm format (datetime-local compatible)
  onChange?: (value: string | undefined) => void;
  placeholder?: string;
  className?: string;
}

export function DateTimePicker({
  value,
  onChange,
  placeholder = "Pick date and time",
  className,
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false);

  // Parse string value to Date object and time components
  const { dateValue, hours, minutes } = React.useMemo(() => {
    if (!value) return { dateValue: undefined, hours: "12", minutes: "00" };
    try {
      const date = parse(value, "yyyy-MM-dd'T'HH:mm", new Date());
      return {
        dateValue: date,
        hours: format(date, "HH"),
        minutes: format(date, "mm"),
      };
    } catch {
      return { dateValue: undefined, hours: "12", minutes: "00" };
    }
  }, [value]);

  // Build the datetime string from components
  const buildDateTimeString = (
    date: Date | undefined,
    h: string,
    m: string
  ): string | undefined => {
    if (!date) return undefined;
    const dateStr = format(date, "yyyy-MM-dd");
    return `${dateStr}T${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
  };

  // Handle date selection
  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      onChange?.(buildDateTimeString(date, hours, minutes));
    } else {
      onChange?.(undefined);
    }
  };

  // Handle time changes
  const handleHoursChange = (newHours: string) => {
    if (dateValue) {
      onChange?.(buildDateTimeString(dateValue, newHours, minutes));
    }
  };

  const handleMinutesChange = (newMinutes: string) => {
    if (dateValue) {
      onChange?.(buildDateTimeString(dateValue, hours, newMinutes));
    }
  };

  // Generate hour options (00-23)
  const hourOptions = Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, "0")
  );

  // Generate minute options (00, 15, 30, 45)
  const minuteOptions = ["00", "15", "30", "45"];

  // Handle clear
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(undefined);
    setOpen(false);
  };

  return (
    <div className="relative w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal h-[42px]",
              !dateValue && "text-muted-foreground",
              dateValue && "pr-10",
              className
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
            <span className="flex-1 truncate text-left">
              {dateValue
                ? format(dateValue, "MMM d, yyyy") + ` at ${hours}:${minutes}`
                : placeholder}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={dateValue}
          onSelect={handleDateSelect}
          initialFocus
        />
        <div className="border-t p-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Time:</span>
            <Select value={hours} onValueChange={handleHoursChange}>
              <SelectTrigger className="w-[70px]">
                <SelectValue placeholder="HH" />
              </SelectTrigger>
              <SelectContent>
                {hourOptions.map((hour) => (
                  <SelectItem key={hour} value={hour}>
                    {hour}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm">:</span>
            <Select value={minutes} onValueChange={handleMinutesChange}>
              <SelectTrigger className="w-[70px]">
                <SelectValue placeholder="MM" />
              </SelectTrigger>
              <SelectContent>
                {minuteOptions.map((minute) => (
                  <SelectItem key={minute} value={minute}>
                    {minute}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </PopoverContent>
    </Popover>
      {dateValue && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
